import { ChangeDetectionStrategy, Component, OnInit, ViewChild, ElementRef, Input, OnDestroy, signal } from '@angular/core';
import { ModalController, PopupWindow, CommandService } from 'flux-core';
import { Subscription, from, of, Observable } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { UserCommandEvent } from '../../user/command/user-command-event';
import { StateService, Tracker } from 'flux-core';
import { ToolTip } from 'flux-core/src/ui';
import Cookies from 'js-cookie';

/**
 * Demo survey dialog window. This will handle the popup,
 * And show the demo survey window.
 *
 * @author  Sameera
 * @since   2025-05-23
 */

@Component({
    templateUrl: './demo-survey-window.cmp.html',
    selector: 'demo-survey-window',
    styleUrls: [ './demo-survey-window.cmp.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DemoSurveyWindow extends PopupWindow implements OnDestroy, OnInit {

    /**
     * Tracks which emoji is currently selected
     */
    public selectedEmoji: number | null = null;

    /**
     * Tracks which emoji has the temporary active state
     * for animation purposes
     */
    public activeEmojiId: number | null = null;

    /**
     * Tracks the selected rating reason
     */
    public selectedRatingReason: string | null = null;

    private autoCloseTimeout: any = null;

    /**
     * Array of emojis with their data
     */
    public emojis = [
        {
            id: 1,
            alt: 'Nope, not for me',
            ratingReasons: {
                heading: 'Ouch! Help us do better.Even a one-liner helps.',
                options: [
                    'Hard to use',
                    'Performance issues',
                    'Other'
                ]
            },
            textArea: {
                placeholder: 'Tell us what felt off…'
            }
        },
        {
            id: 2,
            alt: 'Could Be Better',
            ratingReasons: {
                heading: 'We can improve! What held you back?',
                options: [
                    'Hard to use',
                    'Performance issues',
                    'Other'
                ]
            },
            textArea: {
                placeholder: 'What should we fix first?'
            }
        },
        {
            id: 3,
            alt: "Meh, It's Fine",
            ratingReasons: {
                heading: 'Thanks! How could we level up?',
                options: [
                    'Hard to use',
                    'Performance issues',
                    'Other'
                ]
            },
            textArea: {
                placeholder: 'Any small tweaks in mind?'
            }
        },
        {
            id: 4,
            alt: 'Pretty Sweet',
            ratingReasons: {
                heading: 'Lovely! What stood out?',
                options: [
                    'Easy to use',
                    'Good templates',
                    'Other'
                ]
            },
            textArea: {
                placeholder: "Anything else you liked?"
            }
        },
        {
            id: 5,
            alt: 'Crushing It !',
            ratingReasons: {
                heading: 'Pumped! What did you love?',
                options: [
                   'AI features',
                   'Easy To use',
                   'Other'
                ]
            },
            textArea: {
                placeholder: "Shout-outs or wishes…"
            }
        },
    ];

    /**
     * Tracks whether the feedback has been sent
     */
    public feedbackSent: boolean = false;

    public showEmojiSection = signal<boolean>(true);

    public showRatingReasons = signal<boolean>(false);

    public showFeedbackTextArea = signal<boolean>(false);

    public activeStep = signal<number>(1);

    /**
     * Tracks the number of changes made in the diagram
     */
    @Input() public diagramChangesCount: number = 0;

    /**
     * Gets the id of the template if any
     */
    @Input() public templateId: string = '';

    /**
     * Session length in minutes from app start to survey popup
     */
    @Input() public sessionLengthMinutes: number = 0;

    /**
     * User's country code
     */
    protected userCountry: string = '';

    /**
     * An array containing all subscriptions.
     */
    protected subs: Array<Subscription>;

    protected timeoutOnEmojiClick: any = null;

    protected timeoutOnRatingReasonClick: any = null;

    /**
     * The wrapper container.
     */
    @ViewChild( 'window', { static: true })
    protected window;

    /**
     * The file exported window container.
     */
    @ViewChild( 'container', { static: true })
    protected container;

    /**
     * Reference to the feedback textarea element
     */
    @ViewChild('feedbackText', { static: false })
    protected feedbackText: ElementRef;

    /**
     * This holds the emoji tooltip
     */
    @ViewChild( 'emojiTooltip', { read: ToolTip })
    public emojiTooltip: ToolTip;

    constructor(
        protected commandService: CommandService,
        protected modalController: ModalController,
        public translate: TranslateService,
        protected state: StateService< any, any >,
    ) {
        super();
        this.subs = [];
    }

    public ngOnInit(): void {
        this.subs.push(
            this.showWindow( this.container, this.window ).subscribe(),
        );
        Tracker.track( 'dialog.demoSurvey.load', {
            diagram_changes_count: this.diagramChangesCount.toString(),
            template_id: this.templateId || '',
            session_length: this.sessionLengthMinutes.toString() + 'min' || '',
        });
    }

    /**
     * Close the modal window
     */
    public closeWindow() {
        const sub = this.hideWindow( this.container, this.window ).subscribe({
            complete: () => {
                this.modalController.hide();
            },
        });
        this.subs.push( sub );
    }

    /**
     * Unsubscribe the subscription when component destroy
     */
    public ngOnDestroy(): void {
        if (this.autoCloseTimeout) {
            clearTimeout(this.autoCloseTimeout);
            this.autoCloseTimeout = null;
        }
        if (this.timeoutOnEmojiClick) {
            clearTimeout(this.timeoutOnEmojiClick);
            this.timeoutOnEmojiClick = null;
        }
        if (this.timeoutOnRatingReasonClick) {
            clearTimeout(this.timeoutOnRatingReasonClick);
            this.timeoutOnRatingReasonClick = null;
        }
        while ( this.subs.length > 0 ) {
            this.subs.pop().unsubscribe();
        }
    }

    /**
     * Handle the close button click event
     * call the user preference update command and update
     * the whats_new_last_version value with current config value
     */
    public closeClick(closeBtn?: boolean) {
        this.closeWindow();

        localStorage.setItem('demoSurveySeen', 'true');

        if (closeBtn) {
            Tracker.track( 'dialog.demoSurvey.close', {
                diagram_changes_count: this.diagramChangesCount.toString(),
                template_id: this.templateId || '',
                session_length: this.sessionLengthMinutes.toString() + 'min' || '',
            });
        }
    }

    /**
     * Handle emoji hover events to change images between default and colored versions
     * @param emojiNumber The emoji number (1-5)
     * @param isHovered Whether the emoji is being hovered over (true) or not (false)
     */
    public onEmojiHover(emojiNumber: number, isHovered: boolean): void {
        // Skip hover effect if this emoji is already selected
        if (this.selectedEmoji === emojiNumber) {
            return;
        }

        const emojiImg = document.querySelector(`.demo-survey-window-emoji-wrapper:nth-child(${emojiNumber}) img`);
        if (emojiImg) {
            const basePath = './assets/images/modal-windows/demo-survey/';
            const imgSrc = isHovered
                ? `${basePath}emoji-${emojiNumber}-colored.svg`
                : `${basePath}emoji-${emojiNumber}.svg`;

            (emojiImg as HTMLImageElement).src = imgSrc;
        }
    }

    /**
     * Handle emoji click events to select an emoji and keep it colored
     * @param emojiNumber The emoji number (1-5) that was clicked
     */
    public onEmojiClick(emojiNumber: number): void {
        this.selectedEmoji = emojiNumber;
        this.selectedRatingReason = null;

        // Set the active state for the clicked emoji
        this.activeEmojiId = emojiNumber;

        this.timeoutOnEmojiClick = setTimeout(() => {
            this.activeEmojiId = null;
            this.showEmojiSection.set(false);
            this.showRatingReasons.set(true);
            this.activeStep.set(2);
        }, 500);

        // Update all emoji images to ensure only the selected one is colored
        for (let i = 1; i <= 5; i++) {
            const emojiImg = document.querySelector(`.demo-survey-window-emoji-wrapper:nth-child(${i}) img`);
            if (emojiImg) {
                const basePath = './assets/images/modal-windows/demo-survey/';
                const imgSrc = (i === this.selectedEmoji)
                    ? `${basePath}emoji-${i}-colored.svg`
                    : `${basePath}emoji-${i}.svg`;

                (emojiImg as HTMLImageElement).src = imgSrc;
            }
        }

        Tracker.track('dialog.demoSurvey.emoji.click', {
            rating: this.selectedEmoji.toString(),
            diagram_changes_count: this.diagramChangesCount.toString(),
            template_id: this.templateId || '',
            session_length: this.sessionLengthMinutes.toString() + 'min' || '',
        });
    }

    /**
     * Gets the data for the currently selected emoji
     * @returns The emoji data object or undefined if none selected
     */
    public getSelectedEmojiData() {
        return this.selectedEmoji ? this.emojis.find(emoji => emoji.id === this.selectedEmoji) : undefined;
    }

    /**
     * Handle selection of a rating reason
     * @param reason The reason that was selected
     */
    public selectRatingReason(reason: string): void {
        this.selectedRatingReason = reason;

        this.timeoutOnRatingReasonClick = setTimeout(() => {
            this.showRatingReasons.set(false);
            this.showFeedbackTextArea.set(true);
        }, 500);

        Tracker.track('dialog.demoSurvey.ratingReason.click', {
            rating: this.selectedEmoji.toString(),
            ratingReason: reason,
            diagram_changes_count: this.diagramChangesCount.toString(),
            template_id: this.templateId || '',
            session_length: this.sessionLengthMinutes.toString() + 'min' || '',
        });
    }

    /**
     * Handle sending the feedback when the user clicks the Send Feedback button
     */
    public sendFeedback(): void {
        if (!this.selectedEmoji) {
            return;
        }

        this.feedbackSent = true;

        this.autoCloseTimeout = setTimeout(() => {
            this.closeWindow();
        }, 5000);

        localStorage.setItem('demoSurveySeen', 'true');

        const feedbackTextValue = this.feedbackText?.nativeElement?.value || '';

        // Get browser and OS info
        const browserInfo = this.getBrowserInfo();
        const osInfo = this.getOSInfo();

        const sub = this.fetchUserCountry().pipe(
            tap(country => {
                this.commandService.dispatch(UserCommandEvent.submitDemoSurvey, {
                    uid: this.state.get('CurrentUser'),
                    rating: this.selectedEmoji,
                    feedback: feedbackTextValue,
                    ratingReason: this.selectedRatingReason || '',
                    diagramChangesCount: this.diagramChangesCount,
                    firstLandingPage: Cookies.get('first_landing_page'),
                    browser: browserInfo.name + ' ' + browserInfo.version,
                    os: osInfo,
                    templateId: this.templateId,
                    country: country,
                    sessionLengthMinutes: this.sessionLengthMinutes,
                });

                Tracker.track('dialog.demoSurvey.feedback.submit', {
                    rating: this.selectedEmoji.toString(),
                    rating_reason: this.selectedRatingReason || '',
                    feedback: feedbackTextValue,
                    diagram_changes_count: this.diagramChangesCount.toString(),
                    template_id: this.templateId || '',
                    session_length: this.sessionLengthMinutes.toString() + 'min' || '',
                });
            })
        ).subscribe();

        this.subs.push(sub);
    }

    /**
     * Gets the user's browser information
     * @returns Object containing browser name and version
     */
    private getBrowserInfo(): { name: string; version: string } {
        const userAgent = navigator.userAgent;
        let browserName = 'Unknown';
        let browserVersion = '';

        // Detect Chrome
        if (/Chrome/.test(userAgent) && !/Chromium|Edge|Edg|OPR|Opera/.test(userAgent)) {
            browserName = 'Chrome';
            browserVersion = userAgent.match(/Chrome\/(\d+\.\d+)/)?.[1] || '';
        }
        // Detect Firefox
        else if (/Firefox/.test(userAgent)) {
            browserName = 'Firefox';
            browserVersion = userAgent.match(/Firefox\/(\d+\.\d+)/)?.[1] || '';
        }
        // Detect Safari
        else if (/Safari/.test(userAgent) && !/Chrome|Chromium|Edge|Edg|OPR|Opera/.test(userAgent)) {
            browserName = 'Safari';
            browserVersion = userAgent.match(/Version\/(\d+\.\d+)/)?.[1] || '';
        }
        // Detect Edge
        else if (/Edge|Edg/.test(userAgent)) {
            browserName = 'Edge';
            browserVersion = userAgent.match(/Edge\/(\d+\.\d+)/)?.[1] || userAgent.match(/Edg\/(\d+\.\d+)/)?.[1] || '';
        }
        // Detect Opera
        else if (/OPR|Opera/.test(userAgent)) {
            browserName = 'Opera';
            browserVersion = userAgent.match(/OPR\/(\d+\.\d+)/)?.[1] || userAgent.match(/Opera\/(\d+\.\d+)/)?.[1] || '';
        }

        return { name: browserName, version: browserVersion };
    }

    /**
     * Gets the user's operating system information
     * @returns OS name
     */
    private getOSInfo(): string {
        const userAgent = navigator.userAgent;
        let osName = 'Unknown';

        if (/Windows/.test(userAgent)) {
            osName = 'Windows';
            if (/Windows NT 10.0/.test(userAgent)) osName = 'Windows 10/11';
            else if (/Windows NT 6.3/.test(userAgent)) osName = 'Windows 8.1';
            else if (/Windows NT 6.2/.test(userAgent)) osName = 'Windows 8';
            else if (/Windows NT 6.1/.test(userAgent)) osName = 'Windows 7';
        } else if (/Macintosh|Mac OS X/.test(userAgent)) {
            osName = 'macOS';
        } else if (/Linux/.test(userAgent)) {
            osName = 'Linux';
        } else if (/Android/.test(userAgent)) {
            osName = 'Android';
        } else if (/iOS|iPhone|iPad|iPod/.test(userAgent)) {
            osName = 'iOS';
        }

        return osName;
    }

    /**
     * Fetch the user's country
     * @returns Observable that emits the country name
     */
    private fetchUserCountry(): Observable<string> {
        return from(fetch('https://api.country.is').then(res => res.json()))
            .pipe(
                catchError(err => {
                    console.error('Error fetching country:', err);
                    return of({ country: '' });
                }),
                map(countryInfo => {
                    try {
                        const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
                        this.userCountry = regionNames.of(countryInfo.country);
                    } catch (e) {
                        // If Intl.DisplayNames fails, just use the country code
                        this.userCountry = countryInfo.country;
                    }
                    return this.userCountry;
                })
            );
    }
}
